import React, { useEffect } from 'react'
import { Alert, Box, TextBody, get, upToMd } from '@ally/metronome-ui'
import styled from 'styled-components'
import { TextInput } from '@ally/forms'
import { useAriaLive } from '@ally/use-aria-live'
import { Private } from '@ally/private'
import { Beneficiary } from '../../types'

const StyledBox = styled(Box)`
  border-bottom: 1px solid ${get.colors('slate-2')};
`

export const FundsAllocationFields: React.FC<{
  existingBeneficiaries?: Beneficiary[]
  total: number
  hideNewBeneAllocationInput?: boolean
  setPercentageValue: (name: string, value: string) => void
}> = ({
  existingBeneficiaries,
  total,
  hideNewBeneAllocationInput,
  setPercentageValue,
}) => {
  const handleNewBeneficiaryFundAllocationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { value } = event.target
    setPercentageValue('newBeneficiaryFundAllocation', value)
  }
  const { announceAssertive } = useAriaLive()

  useEffect(() => {
    if (total !== 100) {
      announceAssertive('Your total allocation must add up to 100%')
    }
  }, [total])

  const handlePercentageOnBlur = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { name, value } = e.target
    setPercentageValue(name, value)
  }

  const validatePercentField = (value: string): string => {
    const regex = /[^0-9]/g
    if (regex.test(value))
      return 'Whoops. Make sure you enter a whole number greater than 0.'
    if (!value || Number(value) === 0)
      return `Whoops. Make sure you enter a whole number greater than 0.`
    if (Number(value) > 100)
      return 'Whoops. Make sure you enter a whole number 100 or lower.'
    return ''
  }

  return (
    <>
      <StyledBox>
        {!hideNewBeneAllocationInput && (
          <Box width={upToMd('40%', '30%')} my="md">
            <TextInput
              labelContent="New Beneficiary"
              name="newBeneficiaryFundAllocation"
              inputType="text"
              validate={validatePercentField}
              data-testid="new-bene-fund-allocation-input"
              adornment={{
                variant: 'custom',
                item: {
                  text: '%',
                  variant: 'static',
                },
                position: 'right',
              }}
              onFocus={
                /* istanbul ignore next */ (e: any): void => e.target.select()
              }
              onChange={handleNewBeneficiaryFundAllocationChange}
            />
          </Box>
        )}
        {existingBeneficiaries &&
          existingBeneficiaries.length &&
          existingBeneficiaries.map(bene => (
            <Box width={upToMd('40%', '30%')} my="md" key={bene.beneficiaryId}>
              <TextInput
                labelContent={
                  <Private>
                    {bene.beneficiaryName
                      ? bene.beneficiaryName
                      : `${bene.firstName} ${bene.middleName || ''} ${
                          bene.lastName
                        }`}
                  </Private>
                }
                name={bene.beneficiaryId}
                inputType="text"
                validate={validatePercentField}
                onBlur={handlePercentageOnBlur}
                data-testid={`${bene.beneficiaryId}-fund-allocation-input`}
                adornment={{
                  variant: 'custom',
                  item: {
                    text: '%',
                    variant: 'static',
                  },
                  position: 'right',
                }}
                onFocus={
                  /* istanbul ignore next */ (e: any): void => e.target.select()
                }
              />
            </Box>
          ))}
      </StyledBox>
      <Box my="md">
        <TextBody size="sm" tag="div" weight="bold">
          Total: {total}%
        </TextBody>
      </Box>
      {total !== 100 && (
        <Box mb="xl">
          <Alert
            variant="warning"
            disableContainer
            heading="Your total allocation must add up to 100%."
          />
        </Box>
      )}
    </>
  )
}
